/* The 604px side dock the day form lives in, and the library column beside it.
 *
 * Promoted out of `pages/plan-builder` on its second consumer: the assigned-plan calendar hosts the
 * SAME form on one cycle of a day, in the same box. Two page-local copies of these four blocks is
 * exactly the near-duplicate-class failure the house rule exists to prevent — they would differ by
 * two pixels within a round, and the two docks would stop being the same surface.
 *
 * Nothing here is redeclared by either consumer. A declaration living in both a shared module and a
 * page's own is decided by chunk order, which differs between the dev server and the build, so the
 * dev render and the shipped render can disagree. The builder's fullscreen host stays in its own
 * stylesheet because only one host has one.
 *
 * ── The panel is the gallery's own third column ─────────────────────────────────────────────
 * `Tydzien z panelem dnia` lays the specimen out as week → 240px library → 604px day panel, all
 * three in normal flow. This app keeps the panel `position: fixed` over the canvas, so nothing
 * behind it reflows when it opens, and gives it the elevation that difference costs — a HORIZONTAL
 * cast, because the panel arrives from the right edge and a downward card shadow under a full-height
 * surface reads as a seam rather than as a layer. Neutral, never coloured.
 */

/* The panel's width. The library column is a CHILD of the popup — see either dock's own header for
 * why — so it inherits this rather than redeclaring it. */
.host {
  --dock-width: 604px;

  /* ── The dock inherits the BROWSER's leading, not the app's prose leading ──────────────────
   * `body` sets `--lh-relaxed` (1.5) app-wide, which is right for prose and wrong for a dense
   * tool. The 02.08 gallery's specimens declare no line-height at all, so every one of their
   * micro-labels is measured at the browser's `normal` — 10px → 12, 11px → 13, 9.5px → 11.
   *
   * Measured rather than assumed: forty-odd strings in this dock were inheriting 1.5, which is
   * three pixels of leading each. It accumulated down the session bar and the load module until
   * the library rail beside them started 3.5px below where the gallery draws it, and it pushed
   * the day's own header rows apart by the same amount. One declaration here rather than forty
   * `line-height` lines, because the RULE is "this surface is dense", not "this label is".
   *
   * Every element that wants something else already says so — the exercise note's 1.45, the set
   * type badge's 1, the exercise name's 1.28 — and an explicit declaration is untouched by an
   * inherited one. */
  line-height: var(--lh-auto);
}

.panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  display: flex;
  width: var(--dock-width);
  max-width: 100vw;
  background: var(--bg-sunken);
  border-left: var(--border-hairline) solid var(--border-2);
  box-shadow: var(--shadow-sheet);
}

/* The library column, between the canvas and the panel — "Biblioteka siedzi między nimi, bo jest
 * źródłem przeciągania w prawo". Fixed for the same reason the panel is.
 *
 * It holds the programator's own 320px DRAWER since 19 September 2026 (owner): the same component
 * with the same search, filter tabs, chips, multi-select foot and hover peek, so a trainer meets
 * one library in this app and not two. What is different here — and the owner said so — is that it
 * unfolds OUTWARD, to the left of the dock and outside it, rather than sliding inward over the day.
 * That is why the day form behind it stays visible and still takes a drop. */
.railHost {
  position: fixed;
  top: 0;
  right: var(--dock-width);
  bottom: 0;
  z-index: 20;
  display: flex;
  width: var(--w-lib-drawer);
  background: var(--bg-card);
  border-left: var(--border-hairline) solid var(--border);
}

/* The dock's live region. Always in the accessibility tree and empty when there is nothing to say —
 * a region inserted together with its text is not reliably announced, and every act a dock performs
 * is one that has to be. `clip-path` rather than `display: none`, which would take it out of the
 * tree entirely. */
.announcer {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
